import { Phone, User } from 'lucide-react';
import { unitTotalsOf, type AwaitingPickupRow } from '@eombor/contracts';
import { Badge, Button, Dialog, cn } from '@eombor/ui';
import { t } from '../../../shared/i18n/index.js';
import { requisitionStatusText, unitText } from '../../../shared/i18n/labels.js';

/**
 * «NEGA OLMADINGIZ?» oynasi.
 *
 * Ro'yxatdagi satr savolni tug'diradi, javob esa boshqa sahifada
 * qolardi: rahbar talabnomani ochish uchun bo'limni topib, hujjatni
 * qidirib yurishi kerak edi. Bu yerda esa suhbat uchun kerak bo'lgan
 * hamma narsa bitta joyda — kim, qaysi lavozim, qaysi bo'lim, telefon
 * raqami, nima uchun so'ragan va aniq nima band turibdi.
 *
 * Telefon `tel:` havolasi: ish stolida ham, telefonda ham bosilsa
 * qo'ng'iroqqa o'tadi — raqamni qo'lda ko'chirish ortiqcha qadam.
 *
 * Oyna hech qanday so'rov YUBORMAYDI: ma'lumot ro'yxat bilan birga
 * kelgan. Shu sababli u bir zumda ochiladi va yuklanish holati yo'q.
 */

const format = (value: number): string => value.toLocaleString('ru-RU');

const dateText = (at: Date): string => {
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${pad(at.getDate())}.${pad(at.getMonth() + 1)}.${at.getFullYear()}`;
};

/**
 * Muddat — sana va u O'TGANMI.
 *
 * Zamon shu yerda hal bo'ladi: muddat hali kelmagan bo'lsa «kerak»,
 * o'tgan bo'lsa «muddati o'tgan». Bitta o'tgan zamonli matn ikkala
 * holatga ham yozilsa, hali kelmagan muddat ham kechikkandek
 * ko'rinardi — va aksincha, haqiqiy kechikish e'tiborga tushmasdi.
 *
 * Kun boshi bilan solishtiriladi: muddat KUNLARDA yashaydi va
 * «bugun kerak» degan hujjat bugun kechikkan emas.
 */
const deadlineOf = (iso: string | null): { text: string; overdue: boolean } | null => {
  if (iso === null) return null;
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const overdue = at.getTime() < today.getTime();

  return {
    text: overdue
      ? t('report.awaitOverdue', { date: dateText(at) })
      : t('report.awaitNeededBy', { date: dateText(at) }),
    overdue,
  };
};

/** «120 dona · 5 m» — birliklar ALOHIDA, qo'shilmaydi. */
const totalsText = (row: AwaitingPickupRow): string =>
  unitTotalsOf(row.lines)
    .map((total) => `${format(total.qty)} ${unitText(total.unit)}`)
    .join(' · ');

const Field = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div className="flex min-w-0 flex-col gap-0.5">
    <dt className="text-xs text-text-faint">{label}</dt>
    <dd className="text-sm text-text">{value}</dd>
  </div>
);

export interface AwaitingDetailDialogProps {
  row: AwaitingPickupRow | null;
  onClose: () => void;
  /** Talabnoma sahifasiga o'tish — berilmasa tugma chizilmaydi. */
  onOpenRequisition?: (requisitionId: string) => void;
}

export const AwaitingDetailDialog = ({
  row,
  onClose,
  onOpenRequisition,
}: AwaitingDetailDialogProps) => {
  if (row === null) return null;

  const deadline = deadlineOf(row.neededBy);

  return (
    <Dialog
      open
      onClose={onClose}
      size="lg"
      title={row.docNo}
      description={t('report.awaitDialogHint', { days: row.waitingDays })}
      footer={
        onOpenRequisition === undefined ? null : (
          <Button
            variant="primary"
            onClick={() => {
              /* Avval YOPILADI: oyna yangi sahifa ustida osilib
                 qolmasligi kerak (`AcceptQueue` bilan ayni qoida). */
              onClose();
              onOpenRequisition(row.requisitionId);
            }}
          >
            {t('report.awaitOpenDoc')}
          </Button>
        )
      }
    >
      <div className="flex flex-col gap-5">
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone="warn">{requisitionStatusText(row.status)}</Badge>
          {/* Muddat o'tgani ALOHIDA aytiladi: sana yolg'iz turganda
              uni bugungi kun bilan odam solishtirishi kerak bo'lardi. */}
          {deadline === null ? null : (
            <span className={cn('text-xs', deadline.overdue ? 'text-danger' : 'text-text-muted')}>
              {deadline.text}
            </span>
          )}
        </div>

        {/* KIM — oynaning sababi. Eng tepada va eng ko'zga tashlanadigan. */}
        <section className="flex flex-col gap-3 rounded-[var(--radius-md)] border border-border bg-surface-2 px-4 py-3.5">
          <h3 className="flex items-center gap-2 text-xs font-semibold tracking-wide text-text-muted uppercase">
            <User size={13} aria-hidden />
            {t('report.awaitWho')}
          </h3>

          <dl className="grid gap-3 sm:grid-cols-2">
            <Field
              label={t('report.awaitRequester')}
              value={
                <span className="flex flex-col">
                  <span className="font-medium">{row.requesterName}</span>
                  {row.requesterPosition === null ? null : (
                    <span className="text-xs text-text-muted">{row.requesterPosition}</span>
                  )}
                </span>
              }
            />
            <Field
              label={t('report.awaitPhone')}
              value={
                row.requesterPhone === null ? (
                  /* Bo'sh katak «yuklanmadi» deb o'qilardi — yo'qligi
                     ochiq aytiladi. */
                  <span className="text-text-faint">{t('report.awaitNoPhone')}</span>
                ) : (
                  <a
                    href={`tel:${row.requesterPhone}`}
                    className="flex items-center gap-1.5 text-accent hover:underline"
                  >
                    <Phone size={14} aria-hidden />
                    {row.requesterPhone}
                  </a>
                )
              }
            />
            {row.orgUnitName === null ? null : (
              <Field label={t('report.awaitOrgUnit')} value={row.orgUnitName} />
            )}
            {row.subdivisionName === null ? null : (
              <Field label={t('report.awaitSubdivision')} value={row.subdivisionName} />
            )}
            {row.throughPerson === null ? null : (
              <Field label={t('report.awaitThrough')} value={row.throughPerson} />
            )}
            <Field label={t('report.awaitWarehouse')} value={row.warehouseName} />
          </dl>
        </section>

        <section className="flex flex-col gap-1">
          <h3 className="text-xs font-semibold tracking-wide text-text-muted uppercase">
            {t('report.awaitPurpose')}
          </h3>
          <p className="text-sm text-text">{row.purpose}</p>
        </section>

        {/* NIMA band turibdi — suhbatda «qaysi mahsulot?» degan savol
            albatta chiqadi va unga javob shu yerda turishi kerak. */}
        <section className="flex flex-col gap-2">
          <h3 className="flex flex-wrap items-baseline gap-2 text-xs font-semibold tracking-wide text-text-muted uppercase">
            {t('report.awaitLines')}
            <span className="tabular text-xs font-normal normal-case text-text">
              {totalsText(row)}
            </span>
          </h3>

          <ul className="flex flex-col divide-y divide-border">
            {row.lines.map((line) => (
              <li
                key={line.nomenclatureId}
                className="flex items-baseline justify-between gap-3 py-1.5"
              >
                <span className="flex min-w-0 items-baseline gap-2">
                  <span className="tabular shrink-0 text-xs text-text-faint">{line.code}</span>
                  <span className="min-w-0 truncate text-sm text-text">{line.name}</span>
                </span>
                <span className="tabular shrink-0 text-sm font-medium text-text">
                  {format(line.qty)}{' '}
                  <span className="font-normal text-text-muted">{unitText(line.unit)}</span>
                </span>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </Dialog>
  );
};
